import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress, Alert } from "@mui/material";
import { startTest, submitTest } from "../api/backend";
import { useTenant } from "../contexts/TenantContext";

const T = {
  primary: "var(--color-primary)",
  primary700: "#182E6E",
  primary10: "rgba(31,58,138,.06)",
  green: "#15803D",
  green50: "rgba(21,128,61,.10)",
  amber: "#B5651D",
  amber50: "rgba(181,101,29,.10)",
  danger: "#B42318",
  ink: "var(--color-ink)",
  ink2: "var(--color-ink-2)",
  ink3: "var(--color-ink-3)",
  ink4: "var(--color-ink-4)",
  line: "var(--color-hairline-cloud)",
  line2: "var(--color-surface-press-light)",
  surface2: "var(--color-surface-2)",
  surface3: "var(--color-surface-press-light)",
  pageBg: "var(--color-surface-canvas-light)",
  night: "var(--color-surface-night)",
  shadowSm: "0 1px 0 rgba(20,24,40,.04), 0 1px 3px rgba(20,24,40,.06)",
};

const TYPE_LABELS = {
  MULTIPLE_CHOICE: "Multiple choice",
  TRUE_FALSE: "True or false",
  MULTIPLE_SELECT: "Select all that apply",
  MATCHING: "Match the pairs",
  SHORT_ANSWER: "Short answer",
};

const LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H"];

// Fisher-Yates — unbiased shuffle, unlike the common sort(() => Math.random() - 0.5)
// anti-pattern. Used to randomize the MATCHING answer-bank pool so its visual order
// no longer lines up 1:1 with the prompt order (grading matches by pair id, not
// position, so a straight-down-the-list placement was always the correct answer).
export function shuffleArray(arr) {
  const result = [...arr];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

const fmtTime = (secs) => {
  if (secs === null || secs === undefined) return "--:--";
  const mm = String(Math.floor(secs / 60)).padStart(2, "0");
  const ss = String(secs % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

const ChevronLeft = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const ChevronRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const FlagIcon = ({ filled }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="4" y1="22" x2="4" y2="15" />
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1" />
  </svg>
);
const ArrowRight = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);
const GripIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="9" cy="6" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="9" cy="18" r="1" />
    <circle cx="15" cy="6" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="18" r="1" />
  </svg>
);
const XIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ─── Btn helpers ─────────────────────────────────────────────────────────────
const btnBase = {
  fontFamily: "inherit",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  cursor: "pointer", border: "none", p: 0, m: 0, background: "none",
};

export default function TestTakePage() {
  const { classId, sessionId, testId } = useParams();
  // Session-less route for class-level tests; never build a /session/undefined path.
  const resultsPath = sessionId
    ? `/classes/${classId}/session/${sessionId}/test/${testId}/results`
    : `/classes/${classId}/tests/${testId}/results`;
  const navigate = useNavigate();
  const { user } = useTenant();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testData, setTestData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [multiAnswers, setMultiAnswers] = useState({});
  const [matchAnswers, setMatchAnswers] = useState({});
  const [shortAnswers, setShortAnswers] = useState({});
  const [flagged, setFlagged] = useState(new Set());
  const [currentQ, setCurrentQ] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [selectedChip, setSelectedChip] = useState(null);

  const dragItem = useRef(null);
  const expiredRef = useRef(false);
  const draftKeyRef = useRef(null);
  const hydratedRef = useRef(false);
  const studentId = user?.id;

  useEffect(() => {
    if (!studentId) return;
    startTest(testId, studentId)
      .then((data) => {
        setTestData(data);
        const sub = data.submission || null;

        // Draft is keyed per attempt (submission id) so a retake never inherits
        // a stale in-progress draft from a previous attempt.
        const draftKey = sub?.id
          ? `testDraft:${testId}:${studentId}:${sub.id}`
          : `testDraft:${testId}:${studentId}`;
        draftKeyRef.current = draftKey;
        try {
          const raw = localStorage.getItem(draftKey);
          if (raw) {
            const draft = JSON.parse(raw);
            if (draft.answers) setAnswers(draft.answers);
            if (draft.multiAnswers) {
              const restored = {};
              Object.entries(draft.multiAnswers).forEach(([qId, arr]) => { restored[qId] = new Set(arr); });
              setMultiAnswers(restored);
            }
            if (draft.matchAnswers) setMatchAnswers(draft.matchAnswers);
            if (draft.shortAnswers) setShortAnswers(draft.shortAnswers);
            if (draft.flagged) setFlagged(new Set(draft.flagged));
            if (draft.currentQ) setCurrentQ(draft.currentQ);
          }
        } catch {
          // corrupt/unavailable draft — ignore and start fresh
        }
        hydratedRef.current = true;

        setLoading(false);
      })
      .catch((err) => {
        setError(err?.response?.data?.error || "Failed to load test. Please try again.");
        setLoading(false);
      });
  }, [testId, studentId]);

  // Persist in-progress answers so a refresh doesn't lose them — the countdown
  // itself already survives a refresh since the server recomputes remainingSeconds on resume.
  useEffect(() => {
    if (!hydratedRef.current || !draftKeyRef.current) return;
    try {
      localStorage.setItem(draftKeyRef.current, JSON.stringify({
        answers,
        multiAnswers: Object.fromEntries(Object.entries(multiAnswers).map(([qId, s]) => [qId, [...s]])),
        matchAnswers,
        shortAnswers,
        flagged: [...flagged],
        currentQ,
      }));
    } catch {
      // storage full/unavailable — draft persistence is best-effort
    }
  }, [answers, multiAnswers, matchAnswers, shortAnswers, flagged, currentQ]);

  // Countdown timer: counts down from the server-computed remainingSeconds (server clock), measured
  // against the browser's own elapsed time only, so clock/timezone skew cannot expire an attempt.
  useEffect(() => {
    const rem = testData?.remainingSeconds;
    if (typeof rem !== "number" || !testData?.timeLimitMinutes) return;
    const deadline = Date.now() + rem * 1000;
    const tick = () => setSecondsLeft(Math.max(0, Math.round((deadline - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [testData?.remainingSeconds, testData?.timeLimitMinutes]);

  // Auto-submit on expire
  useEffect(() => {
    // Only auto-submit when time ran out while the page was open. If it was already up at load
    // (remaining <= 0) the student sees "time is up" and submits explicitly; the server flags overTime.
    if (secondsLeft !== 0 || expiredRef.current || !(testData?.remainingSeconds > 0)) return;
    expiredRef.current = true;
    const list = buildAnswerList();
    submitTest(testId, studentId, list)
      .then(() => {
        if (draftKeyRef.current) localStorage.removeItem(draftKeyRef.current);
        navigate(resultsPath);
      })
      .catch(() => setError("Time expired and auto-submit failed."));
  }, [secondsLeft]); // eslint-disable-line

  // IntersectionObserver — track current visible question
  useEffect(() => {
    if (!testData) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio > 0.35) {
            const q = parseInt(e.target.dataset.qIdx, 10);
            if (!isNaN(q)) setCurrentQ(q);
          }
        });
      },
      { threshold: [0.35, 0.6] }
    );
    document.querySelectorAll("[data-q-idx]").forEach((c) => obs.observe(c));
    return () => obs.disconnect();
  }, [testData]);

  // ── Answer helpers ────────────────────────────────────────────────────────

  const buildAnswerList = () => {
    const list = [];
    for (const [qId, optId] of Object.entries(answers)) list.push({ questionId: qId, selectedOptionId: optId });
    for (const [qId, set] of Object.entries(multiAnswers)) list.push({ questionId: qId, selectedOptionIds: [...set] });
    for (const [qId, slots] of Object.entries(matchAnswers)) {
      list.push({
        questionId: qId,
        matchings: Object.entries(slots).filter(([, a]) => a != null).map(([promptId, answerId]) => ({ promptId, answerId })),
      });
    }
    for (const [qId, text] of Object.entries(shortAnswers)) {
      if (text?.trim()) list.push({ questionId: qId, shortAnswer: text.trim() });
    }
    return list;
  };

  const isAnswered = (q) => {
    const qId = String(q.id);
    if (q.type === "MULTIPLE_SELECT") return (multiAnswers[qId]?.size ?? 0) > 0;
    if (q.type === "MATCHING") {
      const slots = matchAnswers[qId] || {};
      return (q.pairs || []).length > 0 && (q.pairs || []).every((p) => slots[p.id] != null);
    }
    if (q.type === "SHORT_ANSWER") return (shortAnswers[qId] || "").trim().length > 0;
    return answers[qId] != null;
  };

  const toggleFlag = (qId) =>
    setFlagged((prev) => {
      const next = new Set(prev);
      if (next.has(qId)) next.delete(qId); else next.add(qId);
      return next;
    });

  const handleDragStart = (e, qId, answerId, fromPromptId) => {
    dragItem.current = { qId, answerId, fromPromptId };
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDropOnSlot = (e, qId, promptId) => {
    e.preventDefault();
    if (!dragItem.current || dragItem.current.qId !== qId) return;
    const { answerId, fromPromptId } = dragItem.current;
    setMatchAnswers((prev) => {
      const slots = { ...(prev[qId] || {}) };
      if (fromPromptId) slots[fromPromptId] = null;
      Object.keys(slots).forEach((k) => { if (slots[k] === answerId && k !== promptId) slots[k] = null; });
      slots[promptId] = answerId;
      return { ...prev, [qId]: slots };
    });
    dragItem.current = null;
  };

  const handleDropOnPool = (e, qId) => {
    e.preventDefault();
    if (!dragItem.current || dragItem.current.qId !== qId) return;
    const { fromPromptId } = dragItem.current;
    if (fromPromptId) {
      setMatchAnswers((prev) => {
        const slots = { ...(prev[qId] || {}) };
        slots[fromPromptId] = null;
        return { ...prev, [qId]: slots };
      });
    }
    dragItem.current = null;
  };

  const clearMatchSlot = (qId, promptId) =>
    setMatchAnswers((prev) => {
      const slots = { ...(prev[qId] || {}) };
      slots[promptId] = null;
      return { ...prev, [qId]: slots };
    });

  const handleChipTap = (qId, answerId) => {
    if (selectedChip?.qId === qId && selectedChip?.answerId === answerId) {
      setSelectedChip(null);
    } else {
      setSelectedChip({ qId, answerId });
    }
  };

  const handleSlotTap = (qId, promptId) => {
    if (!selectedChip || selectedChip.qId !== qId) return;
    setMatchAnswers((prev) => {
      const slots = { ...(prev[qId] || {}) };
      Object.keys(slots).forEach((k) => { if (slots[k] === selectedChip.answerId) slots[k] = null; });
      slots[promptId] = selectedChip.answerId;
      return { ...prev, [qId]: slots };
    });
    setSelectedChip(null);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await submitTest(testId, studentId, buildAnswerList());
      if (draftKeyRef.current) localStorage.removeItem(draftKeyRef.current);
      navigate(resultsPath);
    } catch (err) {
      setError(err?.response?.data?.error || "Submit failed. Please try again.");
      setSubmitting(false);
      setShowModal(false);
    }
  };

  const scrollToQ = (idx) => {
    document.querySelector(`[data-q-idx="${idx}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setCurrentQ(idx);
  };

  const questions = useMemo(
    () => (testData ? (Array.isArray(testData.questions) ? testData.questions : []) : []),
    [testData],
  );
  const answeredCount = questions.filter(isAnswered).length;
  const flaggedCount = flagged.size;

  // Per-question shuffled answer-bank order for MATCHING questions, keyed by
  // question id. Computed once per test load (not per render/keystroke) so the
  // pool doesn't visually reshuffle while a student is mid-attempt; a fresh page
  // load naturally gets a new shuffle. Prompt rows are intentionally NOT shuffled
  // here — only the answer-bank pool, since that's the side whose position leaked
  // the correct match.
  const matchingAnswerBanks = useMemo(() => {
    const map = {};
    questions.forEach((q) => {
      if (q.type === "MATCHING") {
        // Students get server-shuffled answerChoices with opaque ids (no key in pairs);
        // fall back to shuffling pairs for staff previews that still carry the full pairs.
        if (Array.isArray(q.answerChoices)) map[q.id] = q.answerChoices;
        else if (Array.isArray(q.pairs)) map[q.id] = shuffleArray(q.pairs);
      }
    });
    return map;
  }, [questions]);

  const timeIsUp = secondsLeft === 0 && !(testData?.remainingSeconds > 0);
  const isWarning = secondsLeft !== null && secondsLeft < 120 && secondsLeft > 30;
  const isDanger = secondsLeft !== null && secondsLeft <= 30;
  const timerColor = isDanger ? "#ff8a80" : isWarning ? "var(--color-accent-lime)" : "#fff";
  const timerBg = "transparent";
  const timerBorder = isDanger ? "#ff8a80" : isWarning ? "var(--color-accent-lime)" : "var(--color-hairline-violet)";
  const pulseColor = isDanger ? "#ff8a80" : "var(--color-accent-lime)";

  // ── Loading / error states ────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !testData) {
    return (
      <Box sx={{ maxWidth: 700, mx: "auto", p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Box
          component="button"
          onClick={() => navigate(`/student-hub/${classId}`)}
          sx={{ ...btnBase, height: 42, px: "18px", borderRadius: "10px", border: `1px solid ${T.line}`, background: "#fff", color: T.ink, fontSize: 13.5, fontWeight: 600, "&:hover": { background: T.surface2 } }}
        >
          Back to Session
        </Box>
      </Box>
    );
  }

  // ── Main UI ───────────────────────────────────────────────────────────────

  return (
    <Box sx={{ bgcolor: T.pageBg, minHeight: "100vh" }}>

      {/* ════ STICKY TOPBAR ════ */}
      <Box sx={{
        position: "sticky", top: 0, zIndex: 30,
        background: T.night, color: "#fff",
      }}>
        <Box sx={{
          maxWidth: 1280, mx: "auto", px: { xs: "12px", md: "40px" }, py: "14px",
          display: "grid", gridTemplateColumns: { xs: "auto 1fr auto", md: "auto 1fr auto" }, gap: { xs: "10px", md: "24px" },
          alignItems: "center",
        }}>
          {/* Left: breadcrumb + title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <Box
              component="button"
              onClick={() => navigate(`/student-hub/${classId}`)}
              sx={{ ...btnBase, gap: "6px", fontSize: 13, color: "var(--color-on-dark-muted)", fontWeight: 500, "&:hover": { color: "#fff" } }}
            >
              <ChevronLeft />
              Session
            </Box>
            <Box sx={{ width: 1, height: 28, background: "var(--color-hairline-violet)" }} />
            <Box sx={{ fontSize: 16, fontWeight: 600, color: "#fff", fontFamily: "var(--font-display)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
              {testData?.title}
            </Box>
          </Box>

          {/* Center: question count + passing score */}
          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: "14px", justifySelf: "center", fontSize: 12.5, color: "var(--color-on-dark-muted)", fontWeight: 600, whiteSpace: "nowrap" }}>
            <span><b style={{ color: "#fff", fontWeight: 700 }}>{questions.length}</b> questions</span>
            {testData?.passingScore != null && (
              <>
                <Box sx={{ width: 1, height: 14, background: "var(--color-hairline-violet)" }} />
                <span>Pass at <b style={{ color: "#fff", fontWeight: 700 }}>{testData.passingScore}%</b></span>
              </>
            )}
          </Box>

          {/* Right: timer */}
          {testData?.timeLimitMinutes && secondsLeft !== null ? (
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: "10px",
              px: "14px", py: "9px",
              background: timerBg, border: `1px solid ${timerBorder}`, borderRadius: "12px",
              fontFamily: "var(--font-mono)",
              fontSize: 18, fontWeight: 600, color: timerColor,
              fontVariantNumeric: "tabular-nums", letterSpacing: "-.02em",
              minWidth: 130, justifyContent: "center",
            }}>
              <Box sx={{
                width: 8, height: 8, borderRadius: "50%", background: pulseColor,
                position: "relative", flexShrink: 0,
                "&::before": {
                  content: '""', position: "absolute", inset: -3,
                  borderRadius: "50%", background: pulseColor, opacity: 0.35,
                  animation: `testpulse ${isDanger ? "1s" : "1.8s"} ease-out infinite`,
                },
                "@keyframes testpulse": {
                  "0%": { transform: "scale(.6)", opacity: 0.45 },
                  "100%": { transform: "scale(1.9)", opacity: 0 },
                },
              }} />
              <span>{timeIsUp ? "Time is up" : fmtTime(secondsLeft)}</span>
              <Box component="span" sx={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--color-on-dark-muted)" }}>
                {timeIsUp ? "submit to finish" : "left"}
              </Box>
            </Box>
          ) : <Box />}
        </Box>
        <Box aria-hidden="true" sx={{ height: 3, background: "var(--color-on-dark-faint)" }}>
          <Box sx={{ height: "100%", width: `${questions.length ? (answeredCount / questions.length) * 100 : 0}%`, background: "var(--color-accent-lime)", transition: "width .2s" }} />
        </Box>
      </Box>

      {/* ════ TWO-COLUMN LAYOUT ════ */}
      <Box sx={{
        maxWidth: 1280, mx: "auto", px: { xs: "12px", md: "40px" }, pt: { xs: "16px", md: "28px" }, pb: "120px",
        display: "grid", gridTemplateColumns: { xs: "1fr", md: "240px 1fr" }, gap: { xs: "0", md: "32px" },
        alignItems: "start",
      }}>

        {/* ════ SIDEBAR ════ */}
        <Box component="aside" sx={{
          display: { xs: "none", md: "block" },
          position: "sticky", top: 88,
          p: 0,
        }}>
          <Box sx={{
            fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase",
            color: T.ink3, mb: "12px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <span>Questions</span>
            {flaggedCount > 0 && (
              <Box sx={{
                display: "inline-flex", alignItems: "center", gap: "5px",
                px: "6px", height: 22, borderRadius: "8px",
                background: T.amber50, color: T.amber,
                fontSize: 11, fontWeight: 700, border: `1px solid rgba(181,101,29,.25)`,
              }}>
                <FlagIcon filled />
                {flaggedCount}
              </Box>
            )}
          </Box>

          {/* Question pips */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "6px", mb: "16px" }}>
            {questions.map((q, idx) => {
              const qNum = idx + 1;
              const ans = isAnswered(q);
              const isCur = currentQ === qNum;
              const isFl = flagged.has(String(q.id));
              let bg = "#fff", color = T.ink, border = "var(--color-hairline-cool)", shadow = "none";
              if (isCur) { bg = T.primary; color = "#fff"; border = T.primary; }
              else if (ans) { bg = T.surface3; color = T.ink; border = T.surface3; }
              return (
                <Box key={q.id} component="button" onClick={() => scrollToQ(qNum)} sx={{
                  ...btnBase,
                  aspectRatio: "1", borderRadius: "10px", placeItems: "center", display: "grid",
                  fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 600,
                  background: bg, color, border: `1px solid ${border}`, boxShadow: shadow,
                  transition: "all .12s", position: "relative",
                  "&:hover": !isCur ? { borderColor: T.ink } : {},
                }}>
                  {qNum}
                  {isFl && <Box sx={{ position: "absolute", top: -3, right: -3, width: 9, height: 9, background: T.amber, border: "2px solid var(--color-surface-canvas-light)", borderRadius: "50%" }} />}
                </Box>
              );
            })}
          </Box>

          {/* Legend */}
          <Box sx={{ borderTop: `1px solid ${T.line2}`, pt: "12px", display: "grid", gap: "6px" }}>
            {[
              { label: "Current", sw: { background: T.primary, borderColor: T.primary } },
              { label: "Answered", sw: { background: T.green50, borderColor: "rgba(21,128,61,.25)" } },
              { label: "Not yet answered", sw: {} },
              { label: "Flagged for review", dot: true },
            ].map(({ label, sw, dot }) => (
              <Box key={label} sx={{ display: "flex", alignItems: "center", gap: "8px", fontSize: 11.5, color: T.ink2 }}>
                {dot
                  ? <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: T.amber, mx: "2px" }} />
                  : <Box sx={{ width: 14, height: 14, borderRadius: "4px", border: `1px solid ${T.line}`, ...sw }} />
                }
                {label}
              </Box>
            ))}
          </Box>

          {/* Review & submit CTA */}
          <Box component="button" onClick={() => setShowModal(true)} sx={{
            ...btnBase,
            mt: "14px", width: "100%", gap: "8px",
            height: 38, px: "14px", borderRadius: "10px",
            border: `1px solid ${T.primary}`, background: T.primary, color: "#fff",
            fontSize: 13.5, fontWeight: 600, transition: "background .15s",
            "&:hover": { background: T.primary700 },
          }}>
            Review &amp; submit
            <ArrowRight size={14} />
          </Box>
        </Box>

        {/* ════ MAIN COLUMN ════ */}
        <Box component="main">

          {/* Test header card */}
          <Box sx={{
            background: "#fff", border: `1px solid ${T.line}`, borderRadius: "20px",
            p: { xs: "18px", md: "24px 28px" }, mb: "16px", overflow: "hidden",
          }}>
            <Box>
              <Typography sx={{ fontSize: { xs: 20, md: 26 }, fontWeight: 600, letterSpacing: "-.02em", color: T.ink, lineHeight: 1.15, mb: "6px", fontFamily: "var(--font-display)" }}>
                {testData?.title}
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: T.ink2 }}>
                {testData?.description || "Read each question carefully. You can flag any question to come back to it."}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "12px", mt: "10px", fontSize: 12, color: T.ink3, fontWeight: 500 }}>
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                  <b style={{ color: T.ink, fontWeight: 600 }}>{questions.length}</b> questions
                </Box>
                {testData?.timeLimitMinutes && (
                  <>
                    <Box sx={{ width: 1, height: 14, background: T.line }} />
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                      <b style={{ color: T.ink, fontWeight: 600 }}>{testData.timeLimitMinutes}</b> min
                    </Box>
                  </>
                )}
                {testData?.passingScore != null && (
                  <>
                    <Box sx={{ width: 1, height: 14, background: T.line }} />
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="10" /></svg>
                      Pass at <b style={{ color: T.ink, fontWeight: 600 }}>{testData.passingScore}%</b>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
          )}

          {/* Question cards */}
          {questions.map((q, idx) => {
            const qNum = idx + 1;
            const qId = String(q.id);
            const answered = isAnswered(q);
            const isFlagged = flagged.has(qId);
            const isTF = q.type === "TRUE_FALSE";
            const isMS = q.type === "MULTIPLE_SELECT";
            const isMatch = q.type === "MATCHING";
            const isShort = q.type === "SHORT_ANSWER";

            return (
              <Box key={q.id} data-q-idx={qNum} id={`q-${qNum}`} sx={{
                background: "#fff",
                border: `1px solid ${currentQ === qNum ? "var(--color-hairline-cool)" : T.line}`,
                borderRadius: "20px", p: { xs: "20px", md: "28px" },
                mb: "16px",
                scrollMarginTop: "90px",
                transition: "border-color .2s",
              }}>
                {/* Card header */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", mb: "18px" }}>
                  {/* Row 1: Q-tag + flag */}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                    <Box sx={{
                      display: "inline-flex", alignItems: "center", gap: "8px",
                      background: "transparent", color: T.ink,
                      fontFamily: "var(--font-mono)",
                      fontSize: 12, fontWeight: 700, letterSpacing: ".04em", flexShrink: 0,
                    }}>
                      <span>Q{qNum}</span>
                      <Box component="span" sx={{ color: T.ink4 }}>/</Box>
                      <span>{questions.length}</span>
                      <Box component="span" sx={{ fontFamily: "'Inter', sans-serif", textTransform: "uppercase", letterSpacing: ".08em", fontSize: 10.5, color: T.ink3, fontWeight: 700 }}>
                        · {TYPE_LABELS[q.type] || "Multiple choice"}
                      </Box>
                      {q.points != null && (
                        <Box component="span" sx={{ fontFamily: "'Inter', sans-serif", fontSize: 10.5, fontWeight: 600, color: T.ink3, pl: "8px", ml: "4px", borderLeft: "1px solid rgba(31,58,138,.20)" }}>
                          {q.points} pt{q.points !== 1 ? "s" : ""}
                        </Box>
                      )}
                    </Box>
                    <Box component="button" onClick={() => toggleFlag(qId)} sx={{
                      ...btnBase, gap: "7px",
                      height: 32, px: "12px", borderRadius: "8px",
                      border: `1px solid ${isFlagged ? "rgba(181,101,29,.30)" : T.line}`,
                      background: isFlagged ? T.amber50 : "#fff",
                      color: isFlagged ? T.amber : T.ink3,
                      fontSize: 12.5, fontWeight: 600, transition: "all .15s",
                      "&:hover": { background: isFlagged ? T.amber50 : T.surface2, color: isFlagged ? T.amber : T.ink },
                    }}>
                      <FlagIcon filled={isFlagged} />
                      {isFlagged ? "Flagged" : "Flag"}
                    </Box>
                  </Box>
                  {/* Row 2: Question text */}
                  <Box>
                    <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 500, color: T.ink, letterSpacing: "-.01em", lineHeight: 1.3, m: 0, fontFamily: "var(--font-display)", textWrap: "pretty" }}>
                      {q.questionText}
                    </Typography>
                    {isMS && <Typography sx={{ mt: "6px", fontSize: 13, color: T.ink3, fontStyle: "italic" }}>Select all that apply.</Typography>}
                    {isMatch && <Typography sx={{ mt: "6px", fontSize: 13, color: T.ink3, fontStyle: "italic" }}>Tap a chip to select it, then tap a row to place it. Or drag on desktop.</Typography>}
                    {isShort && q.hint && <Typography sx={{ mt: "6px", fontSize: 13, color: T.ink3, fontStyle: "italic" }}>{q.hint}</Typography>}
                  </Box>
                </Box>

                {/* ── MC and TRUE/FALSE ── */}
                {(q.type === "MULTIPLE_CHOICE" || isTF || !q.type) && (
                  <Box sx={{ display: "grid", gap: "10px", ...(isTF ? { gridTemplateColumns: "1fr 1fr" } : {}) }}>
                    {(q.options || []).map((opt, oIdx) => {
                      const selected = answers[qId] === String(opt.id);
                      return (
                        <Box key={opt.id} component="label" sx={{
                          display: "grid", gridTemplateColumns: "36px minmax(0,1fr) 22px", gap: "16px", alignItems: "center",
                          minHeight: 64, p: "12px 18px 12px 14px",
                          background: selected ? "#fff" : T.pageBg,
                          border: `1px solid ${selected ? T.primary : T.line}`,
                          boxShadow: selected ? `0 0 0 1px ${T.primary}` : "none",
                          borderRadius: "14px", cursor: "pointer", transition: "border-color .15s, background .15s",
                          fontSize: 16, color: T.ink,
                          "&:hover": { borderColor: selected ? T.primary : "var(--color-hairline-cool)" },
                        }}>
                          <input
                            type="radio" name={`q-${qId}`} value={String(opt.id)} checked={selected}
                            onChange={() => setAnswers((prev) => ({ ...prev, [qId]: String(opt.id) }))}
                            style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
                          />
                          <Box sx={{
                            width: 36, height: 36, borderRadius: "10px", display: "grid", placeItems: "center",
                            background: selected ? T.primary : T.surface3, color: selected ? "#fff" : T.ink,
                            fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 600,
                          }}>
                            {LETTERS[oIdx]}
                          </Box>
                          <Box component="span" sx={{ lineHeight: 1.4 }}>{opt.text}</Box>
                          <Box sx={{
                            width: 22, height: 22, borderRadius: "50%", display: "grid", placeItems: "center",
                            border: `1.5px solid ${selected ? T.primary : "var(--color-hairline-cool)"}`,
                            background: selected ? T.primary : "transparent", color: "#fff",
                          }}>
                            {selected && (
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {answered && !isMS && !isShort && !isMatch && (
                  <Box sx={{ mt: "14px", fontSize: 12.5, opacity: 0.55, display: "flex", alignItems: "center", gap: "6px" }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                    Answer saved
                  </Box>
                )}

                {/* ── MULTIPLE_SELECT ── */}
                {isMS && (
                  <Box sx={{ display: "grid", gap: "10px" }}>
                    {(q.options || []).map((opt, oIdx) => {
                      const checked = (multiAnswers[qId] || new Set()).has(String(opt.id));
                      return (
                        <Box key={opt.id} component="label" sx={{
                          display: "grid", gridTemplateColumns: "28px 28px 1fr", gap: "14px",
                          alignItems: "center", minHeight: 64, p: "12px 18px 12px 14px",
                          background: checked ? "#fff" : T.pageBg,
                          border: `1px solid ${checked ? T.primary : T.line}`,
                          boxShadow: checked ? `0 0 0 1px ${T.primary}` : "none",
                          borderRadius: "14px", cursor: "pointer", transition: "border-color .15s, background .15s",
                          fontSize: 16, color: T.ink,
                          "&:hover": { borderColor: checked ? T.primary : "var(--color-hairline-cool)" },
                        }}>
                          <input
                            type="checkbox" checked={checked}
                            onChange={() => setMultiAnswers((prev) => {
                              const s = new Set(prev[qId] || []);
                              if (checked) s.delete(String(opt.id)); else s.add(String(opt.id));
                              return { ...prev, [qId]: s };
                            })}
                            style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
                          />
                          <Box sx={{
                            width: 28, height: 28, borderRadius: "8px", display: "grid", placeItems: "center",
                            background: checked ? T.primary : "#fff",
                            border: `1px solid ${checked ? T.primary : T.line}`,
                            fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700,
                            color: checked ? "#fff" : T.ink3,
                          }}>
                            {LETTERS[oIdx]}
                          </Box>
                          <Box sx={{
                            width: 24, height: 24, borderRadius: "7px",
                            border: `2px solid ${checked ? T.primary : T.line}`,
                            background: checked ? T.primary : "#fff",
                            display: "flex", alignItems: "center", justifyContent: "center", transition: "all .15s",
                          }}>
                            {checked && (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </Box>
                          <Box component="span" sx={{ fontWeight: 500, lineHeight: 1.4 }}>{opt.text}</Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* ── MATCHING ── */}
                {isMatch && (() => {
                  const slots = matchAnswers[qId] || {};
                  const usedIds = new Set(Object.values(slots).filter(Boolean));
                  const activeChip = selectedChip?.qId === qId ? selectedChip.answerId : null;
                  const answerBankPairs = matchingAnswerBanks[qId] || q.pairs || [];
                  return (
                    <Box sx={{ display: "grid", gap: "14px" }}>
                      {/* Answer bank */}
                      <Box sx={{ background: T.surface2, border: `1px dashed ${T.line}`, borderRadius: "12px", p: "14px 16px" }}>
                        <Box sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: T.ink3, mb: "10px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span>Answer bank</span>
                          <Box sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11.5, color: T.ink2, background: "#fff", px: "8px", py: "2px", borderRadius: "6px", border: `1px solid ${T.line}` }}>
                            {answerBankPairs.length - usedIds.size} of {answerBankPairs.length} unused
                          </Box>
                        </Box>
                        {activeChip && (
                          <Box sx={{ mb: "8px", fontSize: 12, color: T.primary, fontWeight: 600, fontStyle: "italic" }}>
                            Tap a row below to place the selected answer.
                          </Box>
                        )}
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: "8px", minHeight: 36 }}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => handleDropOnPool(e, qId)}
                        >
                          {answerBankPairs.map((pair) => {
                            const placed = usedIds.has(pair.id);
                            const isActive = activeChip === pair.id;
                            return (
                              <Box key={pair.id} draggable={!placed}
                                onDragStart={!placed ? (e) => handleDragStart(e, qId, pair.id, null) : undefined}
                                onClick={() => !placed && handleChipTap(qId, pair.id)}
                                sx={{
                                  display: "inline-flex", alignItems: "center", gap: "8px",
                                  p: "9px 14px",
                                  background: isActive ? T.primary : placed ? "transparent" : "#fff",
                                  border: `1.5px solid ${isActive ? T.primary : placed ? "rgba(0,0,0,.1)" : T.line}`,
                                  borderRadius: "999px", fontSize: 13.5, fontWeight: 600,
                                  color: isActive ? "#fff" : placed ? T.ink4 : T.ink,
                                  opacity: placed ? 0.35 : 1,
                                  cursor: placed ? "default" : "pointer",
                                  boxShadow: isActive ? `0 0 0 3px rgba(31,58,138,.20)` : placed ? "none" : T.shadowSm,
                                  transition: "all .15s", userSelect: "none",
                                  "&:hover": placed ? {} : { borderColor: T.primary, color: isActive ? "#fff" : T.primary, transform: "translateY(-1px)" },
                                  "&:active": placed ? {} : { transform: "scale(.97)" },
                                }}>
                                <Box component="span" sx={{ color: isActive ? "rgba(255,255,255,.6)" : T.ink4, display: "inline-flex" }}><GripIcon /></Box>
                                {pair.answer}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>

                      {/* Match rows */}
                      <Box sx={{ display: "grid", gap: "10px" }}>
                        {(q.pairs || []).map((pair, pIdx) => {
                          const assignedId = slots[pair.id];
                          const assignedPair = assignedId ? answerBankPairs.find((p) => p.id === assignedId) : null;
                          const slotHighlight = activeChip && !assignedPair;
                          return (
                            <Box key={pair.id} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr auto 1fr" }, gap: { xs: "6px", sm: "14px" }, alignItems: "center" }}>
                              <Box sx={{
                                p: { xs: "10px 14px", md: "14px 18px" }, background: T.primary10,
                                border: "1.5px solid rgba(31,58,138,.15)", borderRadius: "12px",
                                fontSize: { xs: 14, md: 14.5 }, fontWeight: 600, color: T.ink,
                                display: "flex", alignItems: "center", gap: "12px",
                              }}>
                                <Box sx={{ width: 26, height: 26, borderRadius: "7px", background: "#fff", display: "grid", placeItems: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, color: T.primary, flexShrink: 0 }}>
                                  {LETTERS[pIdx]}
                                </Box>
                                {pair.prompt}
                              </Box>
                              <Box sx={{ color: T.ink4, display: "grid", placeItems: "center", transform: { xs: "rotate(90deg)", sm: "none" } }}>
                                <ArrowRight />
                              </Box>
                              <Box
                                onClick={() => handleSlotTap(qId, pair.id)}
                                onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.background = T.primary10; e.currentTarget.style.borderColor = T.primary; e.currentTarget.style.borderStyle = "solid"; }}
                                onDragLeave={(e) => { e.currentTarget.style.background = ""; e.currentTarget.style.borderColor = ""; e.currentTarget.style.borderStyle = ""; }}
                                onDrop={(e) => { e.currentTarget.style.background = ""; e.currentTarget.style.borderColor = ""; e.currentTarget.style.borderStyle = ""; handleDropOnSlot(e, qId, pair.id); }}
                                sx={{
                                  p: { xs: "10px 14px", md: "14px 18px" }, minHeight: 54,
                                  background: slotHighlight ? "rgba(31,58,138,.04)" : assignedPair ? "#fff" : T.surface2,
                                  border: `1.5px ${slotHighlight ? "solid" : assignedPair ? "solid" : "dashed"} ${slotHighlight ? T.primary : assignedPair ? T.primary : T.line}`,
                                  borderRadius: "12px",
                                  display: "flex", alignItems: "center", gap: "10px",
                                  fontSize: 13.5, color: assignedPair ? T.ink : T.ink3,
                                  fontStyle: assignedPair ? "normal" : "italic",
                                  cursor: activeChip && !assignedPair ? "pointer" : "default",
                                  transition: "all .15s",
                                  "&:hover": slotHighlight ? { background: T.primary10 } : {},
                                }}>
                                {assignedPair ? (
                                  <Box sx={{
                                    display: "inline-flex", alignItems: "center", gap: "8px",
                                    p: "6px 12px", background: T.primary10,
                                    border: "1px solid rgba(31,58,138,.25)", borderRadius: "999px",
                                    fontSize: 13, fontWeight: 600, color: T.primary,
                                  }}>
                                    {assignedPair.answer}
                                    <Box component="span" onClick={(e) => { e.stopPropagation(); clearMatchSlot(qId, pair.id); }}
                                      sx={{ cursor: "pointer", color: T.primary, opacity: 0.6, display: "inline-flex", p: "2px", "&:hover": { opacity: 1 } }}>
                                      <XIcon />
                                    </Box>
                                  </Box>
                                ) : slotHighlight ? "Tap to place here" : "Drop answer here"}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })()}

                {/* ── SHORT ANSWER ── */}
                {isShort && (
                  <Box>
                    <Box
                      component="textarea"
                      placeholder="Type your answer…"
                      maxLength={600}
                      value={shortAnswers[qId] || ""}
                      onChange={(e) => setShortAnswers((prev) => ({ ...prev, [qId]: e.target.value }))}
                      sx={{
                        width: "100%", p: "14px 16px",
                        background: T.surface2, border: `1.5px solid ${T.line}`,
                        borderRadius: "12px", fontFamily: "inherit", fontSize: 14.5, color: T.ink,
                        resize: "vertical", minHeight: 110, transition: "all .15s",
                        "&:focus": { outline: "none", background: "#fff", borderColor: T.primary, boxShadow: "0 0 0 4px rgba(31,58,138,.08)" },
                      }}
                    />
                    <Box sx={{ mt: "6px", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 11.5, color: T.ink3 }}>
                      <span>Be concise. Your answer is graded automatically against the accepted answers (case and extra spaces are ignored).</span>
                      <Box component="span" sx={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        {(shortAnswers[qId] || "").length} / 600
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ════ STICKY FOOTER ════ */}
      <Box sx={{
        position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 25,
        background: "#fff",
        borderTop: `1px solid ${T.line}`,
      }}>
        <Box sx={{
          maxWidth: 1280, mx: "auto", px: { xs: "12px", md: "40px" }, py: { xs: "10px", md: "14px" },
          display: "flex", alignItems: "center", gap: { xs: "8px", md: "18px" }, justifyContent: "space-between",
        }}>
          {/* Left: answered count (hide on mobile) */}
          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: "14px", fontSize: 13, color: T.ink2, flex: 1 }}>
            <span><b style={{ color: T.ink, fontWeight: 700 }}>{answeredCount}</b> of {questions.length} answered</span>
            {flaggedCount > 0 && (
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: "6px", p: "5px 10px", background: T.amber50, border: `1px solid rgba(181,101,29,.25)`, color: T.amber, borderRadius: "999px", fontSize: 11.5, fontWeight: 700 }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" stroke="currentColor" strokeWidth="2.2" /></svg>
                {flaggedCount} flagged
              </Box>
            )}
          </Box>

          {/* Center: prev / counter / next */}
          <Box sx={{ display: "flex", gap: "6px", alignItems: "center" }}>
            <Box component="button" disabled={currentQ <= 1} onClick={() => currentQ > 1 && scrollToQ(currentQ - 1)} sx={{
              ...btnBase, gap: "6px", height: { xs: 38, md: 38 }, px: { xs: "12px", md: "14px" }, borderRadius: "10px",
              border: `1px solid ${T.line}`, background: "#fff", color: T.ink2, fontSize: 13, fontWeight: 600,
              "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
              "&:hover:not(:disabled)": { background: T.surface2, color: T.ink, borderColor: "var(--color-hairline-cool)" },
            }}>
              <ChevronLeft />
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Previous</Box>
            </Box>
            <Box sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5, color: T.ink3, px: "4px", fontWeight: 600, whiteSpace: "nowrap" }}>
              Q<b style={{ color: T.ink, fontWeight: 700 }}>{currentQ}</b> / {questions.length}
            </Box>
            <Box component="button" disabled={currentQ >= questions.length} onClick={() => currentQ < questions.length && scrollToQ(currentQ + 1)} sx={{
              ...btnBase, gap: "6px", height: { xs: 38, md: 38 }, px: { xs: "12px", md: "14px" }, borderRadius: "10px",
              border: `1px solid ${T.line}`, background: "#fff", color: T.ink2, fontSize: 13, fontWeight: 600,
              "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
              "&:hover:not(:disabled)": { background: T.surface2, color: T.ink, borderColor: "var(--color-hairline-cool)" },
            }}>
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Next</Box>
              <ChevronRight />
            </Box>
          </Box>

          {/* Right: save & exit + submit */}
          <Box sx={{ display: "flex", gap: "8px", justifyContent: "flex-end", flex: { md: 1 } }}>
            <Box component="button" onClick={() => navigate(`/student-hub/${classId}`)} sx={{
              ...btnBase, gap: "8px", height: 42, px: { xs: "12px", md: "18px" }, borderRadius: "10px",
              border: `1px solid ${T.line}`, background: "#fff", color: T.ink, fontSize: 13.5, fontWeight: 600, transition: "all .15s",
              "&:hover": { background: T.surface2, borderColor: "var(--color-hairline-cool)" },
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Save &amp; exit</Box>
            </Box>
            <Box component="button" onClick={() => setShowModal(true)} sx={{
              ...btnBase, gap: "8px", height: 46, px: { xs: "14px", md: "22px" }, borderRadius: "10px",
              background: T.primary, color: "#fff", fontSize: { xs: 13, md: 14 }, fontWeight: 600, transition: "background .15s",
              "&:hover": { background: T.primary700 },
            }}>
              Submit <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>test </Box><ArrowRight size={14} />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ════ SUBMIT MODAL ════ */}
      {showModal && (
        <Box
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
          sx={{ position: "fixed", inset: 0, zIndex: 50, background: "rgba(20,24,40,.45)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", p: "24px" }}
        >
          <Box sx={{ background: "#fff", borderRadius: "20px", width: "100%", maxWidth: 480, boxShadow: "0 30px 80px -20px rgba(20,24,40,.4)", overflow: "hidden" }}>
            <Box sx={{ p: "22px 24px 0" }}>
              <Box sx={{ width: 44, height: 44, borderRadius: "12px", background: T.amber50, color: T.amber, display: "grid", placeItems: "center" }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </Box>
              <Typography sx={{ mt: "12px", mb: "6px", fontSize: 22, fontWeight: 600, letterSpacing: "-.01em", color: T.ink, fontFamily: "var(--font-display)" }}>
                Submit your test?
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: T.ink2 }}>
                Once you submit you can't change your answers. Your results are graded automatically and shown right away.
              </Typography>
            </Box>
            <Box sx={{ mx: "24px", mt: "18px", p: "14px 16px", background: T.surface2, border: `1px solid ${T.line}`, borderRadius: "12px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
              {[
                { k: "Answered", v: `${answeredCount} / ${questions.length}`, warn: false },
                { k: "Unanswered", v: questions.length - answeredCount, warn: questions.length - answeredCount > 0 },
                { k: "Flagged", v: flaggedCount, warn: false },
                { k: "Time remaining", v: fmtTime(secondsLeft), warn: false },
              ].map(({ k, v, warn }) => (
                <Box key={k}>
                  <Box sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: T.ink3, mb: "2px" }}>{k}</Box>
                  <Box sx={{ fontSize: 16, fontWeight: 700, color: warn ? T.amber : T.ink, fontVariantNumeric: "tabular-nums", fontFamily: "var(--font-display)" }}>{v}</Box>
                </Box>
              ))}
            </Box>
            <Box sx={{ p: "20px 24px 24px", display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <Box component="button" onClick={() => setShowModal(false)} sx={{
                ...btnBase, height: 42, px: "18px", borderRadius: "10px",
                border: `1px solid ${T.line}`, background: "#fff", color: T.ink, fontSize: 13.5, fontWeight: 600,
                "&:hover": { background: T.surface2 },
              }}>
                Keep working
              </Box>
              <Box component="button" onClick={handleSubmit} disabled={submitting} sx={{
                ...btnBase, height: 42, px: "18px", borderRadius: "10px",
                background: T.primary, color: "#fff", fontSize: 13.5, fontWeight: 600, transition: "background .15s",
                "&:hover:not(:disabled)": { background: T.primary700 },
                "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
              }}>
                {submitting ? "Submitting…" : "Submit final"}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
